import * as React from "react";
import { styled, alpha } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import { Grid, Tab, Tabs, IconButton, Menu, MenuItem, Avatar, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import logo from "./images/logo.png";
import logo2 from "./images/logo2.png";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from "./AuthContext";

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ClearButtonWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 1),
  height: "100%",
  position: "absolute",
  right: 0,
  top: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    paddingRight: `calc(1em + ${theme.spacing(4)})`, // Add right padding for clear button
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up("sm")]: {
      width: "25ch",
      "&:focus": {
        width: "25ch",
      },
    },
  },
}));

const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#073060ff", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#61b8ffff", "#FFFFFF"],
  DET: ["#0C2C56", "#f62e23ff", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#143680ff", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CD6", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#be8e14ff", "#FFFFFF"],
  STL: ["#BE0A15", "#b6b31bff", "#FFFFFF"],
  LAD: ["#005A9D", "#005184ff", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#000000ff", "#FFFFFF"],
  "0": ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};

export default function TopNav({
  setSearchText,
  searchText,
  isDesktop,
  prod,
}: {
  setSearchText: Function;
  searchText: string;
  isDesktop: boolean;
  prod: boolean;
}) {
  const open = true;
  const { user, login, logout } = useAuth();
  const [matches, setMatches] = useState([""]);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const search = () => {
    // Cancel any pending fuzzy search
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();

    fetch(prod ? `https://oriolebird.pythonanywhere.com/search/` : "/search/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(searchText),
    })
      .then((res) => res.json())
      .then((data) => {
        let id = data.id;
        navigate("/player/" + id);
      });
  };

  // Optimized fuzzy search with debounce and cancellation
  useEffect(() => {
    if (!searchText.trim()) {
      setMatches([]);
      return;
    }

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController
    abortControllerRef.current = new AbortController();
    const controller = abortControllerRef.current;

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout with debounce
    timeoutRef.current = setTimeout(() => {
      fetch(
        prod
          ? "https://oriolebird.pythonanywhere.com/search-fuzzy/"
          : `/search-fuzzy/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(searchText),
          signal: controller.signal
        }
      )
        .then((res) => {
          if (!res.ok) throw new Error('Network response was not ok');
          return res.json();
        })
        .then((data) => {
          console.log("Matches: ", data)
          setMatches(data);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error("Search error:", error);
          }
        });
    }, 150); // 300ms debounce delay

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchText, prod]);

  const getValue = () => {
    const path = location.pathname;
    if (path === "/plus") return 0;
    if (path === "/trending") return 1;
    if (path === "/leaders") return 2;
    return false;
  };

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === "Enter") {
      // Cancel any pending debounced search
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Perform immediate search
      search();
      ev.preventDefault();
    }
  };

  const handleClearSearch = () => {
    setSearchText("");
    setMatches([]);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open2 = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  }; 


  return (
    <div>
      {isDesktop && (
        <Box
          sx={{
            flexGrow: 1,
            marginBottom: "25px",
          }}
        >
          <AppBar
            position="static"
            sx={{
              backgroundColor: "#3D5A80",
              overflow: "visible",
              height: "68px",
              maxHeight: "68px",
              boxShadow: "0px 1px 1px black, 0px 3px 3px black",
            }}
          >
            <Toolbar>
              <a href="/leaders">
                <img
                  src={logo}
                  width="600vw"
                  alt=""
                  style={{ maxWidth: "100%" }}
                /></a>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }}
              ></Typography>
              <a href="https://x.com/ProspectSavant" style={{ marginRight: "10px" }}>
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/6/6f/Logo_of_Twitter.svg"
                  height="30px"
                  alt="" />
              </a>
              <Tab label="Donate" {...a11yProps(2)} href="/donate" />
              <Tab label="PS Plus ★" {...a11yProps(6)} href="/plus" sx={{ color: '#FFD700 !important', fontWeight: 'bold', opacity: '1 !important' }} />
              <Tab label="Articles" {...a11yProps(1)} href="/articles" />
              <Tab label="Trending" {...a11yProps(3)} href="/trending" />
              <Tab label="AFL" {...a11yProps(4)} href="/afl/leaders" />
              <Tab label="Spring Training" {...a11yProps(5)} href="/st/leaders" />
              <Tab label="Organizations" {...a11yProps(7)} href="/orgs" />
              <Tab label="Leaderboard" {...a11yProps(0)} href="/leaders" />
              <Grid
                container
                spacing={0}
                columns={1}
                direction="column"
                style={{
                  overflow: "visible",
                  height: "100%",
                  width: "400px",
                  position: "relative",
                }}
              >
                <Search
                  id="search"
                  aria-controls={open ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  style={{ marginTop: "15px" }}
                >
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <StyledInputBase
                    inputProps={{ "aria-label": "search" }}
                    placeholder="Search For a Player"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  {searchText && (
                    <ClearButtonWrapper>
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{ color: "inherit" }}
                        aria-label="clear search"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </ClearButtonWrapper>
                  )}
                </Search>
                {searchText.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 9999,
                      backgroundColor: "#FFFFFF",
                      borderRadius: "0 0 8px 8px",
                      boxShadow: "0px 8px 24px rgba(0,0,0,0.28)",
                      border: "1px solid #B0BEC5",
                      borderTop: "2px solid #3D5A80",
                      overflow: "hidden",
                      maxHeight: "220px",
                      overflowY: "auto",
                    }}
                  >
                    {matches.map((player: any, index: number) => {
                      const rawTeam = player.MLB_AbbName !== undefined && player.MLB_AbbName !== null ? player.MLB_AbbName : "FA"
                      // FanGraphs/older data may still say "OAK"; the site uses "ATH".
                      // Fall back to "FA" for any abbrev we don't have colors for.
                      const normalized = rawTeam === "OAK" ? "ATH" : rawTeam
                      const team = teamColors[normalized] ? normalized : "FA"
                      return (
                        <a key={index} href={"/player/" + player.id} style={{ textDecoration: "none", display: "block" }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              px: 1.5,
                              py: 0.75,
                              borderBottom: "1px solid #EEEEEE",
                              "&:hover": { backgroundColor: "#EEF2F7" },
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ flex: 1, color: "#111111", fontWeight: 500 }}>
                              {player.name}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                width: 36,
                                textAlign: "center",
                                color: "#FFFFFF",
                                backgroundColor: teamColors[team][0],
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                px: 0.5,
                                py: 0.25,
                                borderRadius: "3px",
                                mx: 1,
                                flexShrink: 0,
                              }}
                            >
                              {team}
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: "#666666", width: 70, textAlign: "right", fontSize: "0.75rem", flexShrink: 0 }}>
                              {player.Position}
                            </Typography>
                            {player.seasons && player.seasons.length > 0 && (
                              <Box sx={{ display: "flex", gap: 0.4, ml: 1, flexShrink: 0 }}>
                                {player.seasons.map((s: number) => (
                                  <Typography key={s} variant="caption" sx={{ color: "#3D5A80", fontWeight: 700, fontSize: "0.65rem", lineHeight: 1 }}>
                                    '{String(s).slice(2)}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </a>
                      );
                    })}
                  </Box>
                )}
              </Grid>
              <a href="/leaders">
                <img
                  src={logo2}
                  height="64px"
                  alt=""
                  style={{ marginLeft: "20px" }}
                /></a>
              {/* User avatar / login */}
              <Box sx={{ ml: 1.5, display: "flex", alignItems: "center" }}>
                {user ? (
                  <>
                    <Tooltip title={user.email}>
                      <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
                        <Avatar
                          src={user.picture}
                          alt={user.name}
                          sx={{ width: 34, height: 34, border: user.is_plus ? "2px solid #E9B44C" : "2px solid transparent" }}
                        />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={userMenuAnchor}
                      open={Boolean(userMenuAnchor)}
                      onClose={() => setUserMenuAnchor(null)}
                    >
                      <MenuItem disabled sx={{ fontSize: "0.8rem", color: "#555" }}>{user.name}</MenuItem>
                      <MenuItem onClick={() => { setUserMenuAnchor(null); window.location.href = "/plus"; }}>PS Plus</MenuItem>
                      <MenuItem onClick={() => { setUserMenuAnchor(null); logout(); }}>Sign out</MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Tooltip title="Sign in">
                    <Avatar
                      onClick={login}
                      sx={{ width: 32, height: 32, cursor: "pointer", bgcolor: "#3D5A80", fontSize: "0.75rem" }}
                    >
                      <PersonIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                  </Tooltip>
                )}
              </Box>
            </Toolbar>
          </AppBar>
        </Box>
      )}
      {!isDesktop && (
        <Box sx={{ flexGrow: 1, marginBottom: "25px" }}>
          <AppBar
            position="static"
            sx={{ backgroundColor: "#3D5A80", boxShadow: "0px 2px 1px black" }}
          >
            <Toolbar style={{ display: "flex", flexDirection: "column" }}>
              <Grid
                container
                spacing={1}
                columns={16}
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                marginTop="0px"
              >
                <a href="/leaders">
                  <img
                    src={logo}
                    width="100%"
                    alt=""
                    style={{
                      maxWidth: "100%",
                      boxShadow: "0px 2px 0px black",
                      marginTop: "10px",
                      borderTop: "2px solid black",
                    }}
                  /></a>
                <a href="https://x.com/ProspectSavant" style={{
                  maxWidth: "10%",
                }}>
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/6/6f/Logo_of_Twitter.svg"
                    width="100%"
                    alt=""

                  /></a>
                  
                  <Tabs
                    value={getValue()}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                      minWidth: "max-content",
                      "& .MuiTab-root": {
                        padding: { xs: "6px 8px", sm: "12px 16px" },
                        minWidth: "unset",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        color: "#ABB8C8",
                      },
                      "& .MuiTab-root:first-of-type": {
                        color: "#E9B44C",
                        fontWeight: "bold",
                      },
                    }}
                  >
                    <Tab label="PS Plus ★" {...a11yProps(6)} href="/plus" sx={{ color: '#E9B44C', fontWeight: 'bold' }} />
                    <Tab label="Trending" {...a11yProps(1)} href="/trending" />
                    <Tab label="Leaderboard" {...a11yProps(0)} href="/leaders" />
                    
                    
                  </Tabs>
                    <IconButton
                      aria-label="more"
                      id="long-button"
                      aria-controls={open2 ? 'long-menu' : undefined}
                      aria-expanded={open2 ? 'true' : undefined}
                      aria-haspopup="true"
                      onClick={handleClick}
                    >
                      <MenuIcon sx={{ color: "#ABB8C8" }} />
                    </IconButton>
                    <Menu
                      id="long-menu"
                      anchorEl={anchorEl}
                      open={open2}
                      onClose={handleClose}
                      slotProps={{
                        paper: {
                          sx: {
                            maxHeight: "100vh",
                            minWidth: "180px",
                            maxWidth: "calc(100vw - 32px)",
                          },
                        },
                        list: {
                          'aria-labelledby': 'long-button',
                          sx: {
                            "& .MuiMenuItem-root": {
                              px: 2,
                              py: 1.25,
                              whiteSpace: "nowrap",
                            },
                            "& .MuiMenuItem-root > a": {
                              display: "block",
                              width: "100%",
                            },
                          },
                        },
                      }}
                    >
                        <MenuItem key={'Articles'} onClick={handleClose}>
                          <a href="/articles" style={{ textDecoration: 'none', color: 'inherit' }}>{'Articles'}</a>
                        </MenuItem>
                        <MenuItem key={'Organizations'} onClick={handleClose}>
                          <a href="/orgs" style={{ textDecoration: 'none', color: 'inherit' }}>{'Organizations'}</a>
                        </MenuItem>
                        <MenuItem key={'Spring Training'} onClick={handleClose}>
                          <a href="/st/leaders" style={{ textDecoration: 'none', color: 'inherit' }}>{'Spring Training'}</a>
                        </MenuItem>
                        <MenuItem key={'AFL'} onClick={handleClose}>
                          <a href="/afl/leaders" style={{ textDecoration: 'none', color: 'inherit' }}>{'AFL'}</a>
                        </MenuItem>
                        <MenuItem key={'Donate'} onClick={handleClose}>
                          <a href="/donate" style={{ textDecoration: 'none', color: 'inherit' }}>{'Donate'}</a>
                        </MenuItem>
                        <MenuItem key={'PSPlus'} onClick={handleClose}>
                          <a href="/plus" style={{ textDecoration: 'none', color: '#E9B44C', fontWeight: 'bold' }}>{'PS Plus ★'}</a>
                        </MenuItem>
                    </Menu>
                <Grid
                  container
                  spacing={0}
                  columns={1}
                  direction="column"
                  style={{ marginBottom: "10px", position: "relative" }}
                >
                  <Search>
                    <SearchIconWrapper>
                      <SearchIcon />
                    </SearchIconWrapper>
                    <StyledInputBase
                      inputProps={{ "aria-label": "search" }}
                      placeholder="Search For a Player"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter") {
                          search();
                          ev.preventDefault();
                        }
                      }}
                    />
                    {searchText && (
                      <ClearButtonWrapper>
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          sx={{ color: "inherit" }}
                          aria-label="clear search"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </ClearButtonWrapper>
                    )}
                  </Search>
                  {searchText.length > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 9999,
                        backgroundColor: "#FFFFFF",
                        borderRadius: "0 0 8px 8px",
                        boxShadow: "0px 8px 24px rgba(0,0,0,0.28)",
                        border: "1px solid #B0BEC5",
                        borderTop: "2px solid #3D5A80",
                        overflow: "hidden",
                        maxHeight: "220px",
                        overflowY: "auto",
                      }}
                    >
                      {matches.map((player: any, index: number) => {
                        const rawTeam = player.MLB_AbbName !== undefined && player.MLB_AbbName !== null ? player.MLB_AbbName : "FA"
                        // FanGraphs/older data may still say "OAK"; the site uses "ATH".
                        // Fall back to "FA" for any abbrev we don't have colors for.
                        const normalized = rawTeam === "OAK" ? "ATH" : rawTeam
                        const team = teamColors[normalized] ? normalized : "FA"
                        return (
                          <a key={index} href={"/player/" + player.id} style={{ textDecoration: "none", display: "block" }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                px: 1.5,
                                py: 0.75,
                                borderBottom: "1px solid #EEEEEE",
                                "&:hover": { backgroundColor: "#EEF2F7" },
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ flex: 1, color: "#111111", fontWeight: 500 }}>
                                {player.name}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  width: 36,
                                  textAlign: "center",
                                  color: "#FFFFFF",
                                  backgroundColor: teamColors[team][0],
                                  fontSize: "0.68rem",
                                  fontWeight: 700,
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: "3px",
                                  mx: 1,
                                  flexShrink: 0,
                                }}
                              >
                                {team}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ color: "#666666", width: 70, textAlign: "right", fontSize: "0.75rem", flexShrink: 0 }}>
                                {player.Position}
                              </Typography>
                              {player.seasons && player.seasons.length > 0 && (
                                <Box sx={{ display: "flex", gap: 0.4, ml: 1, flexShrink: 0 }}>
                                  {player.seasons.map((s: number) => (
                                    <Typography key={s} variant="caption" sx={{ color: "#3D5A80", fontWeight: 700, fontSize: "0.65rem", lineHeight: 1 }}>
                                      '{String(s).slice(2)}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </a>
                        );
                      })}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Toolbar>
          </AppBar>
        </Box>
      )}
    </div>
  );
}